import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    Alert,
    Paper,
    Grid,
    List,
    ListItem,
    ListItemText,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Sidebar from '../../components/Sidebar';
import {
    PageContainer,
    ContentContainer,
    Header
} from '../../components/ContentContainer';

import { useProductCSVApproveMutation } from './api/useProductCSVApproveMutation';

const ProductCSVApprovePage = () => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const { mutate, isLoading, error, data } = useProductCSVApproveMutation();

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return;

        try {
            await mutate(file);
        } catch (err) {
            console.error('Failed to upload CSV:', err);
        }
    };

    return (
        <PageContainer>
            <Sidebar navigate={navigate} />
            <ContentContainer>
                <Header>Approve Products via CSV</Header>

                <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Expected CSV format:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        The file must include headers.
                    </Typography>

                    <TableContainer
                        component={Box}
                        sx={{
                            mt: 1.5,
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 1,
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            overflowX: 'auto'
                        }}
                    >
                        <Table size="small" aria-label="csv example table">
                            <TableHead sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>url</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>id</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow sx={{ '&:last-child td': { border: 0 } }}>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.secondary' }}>.../review?session_id=cfe9312c1e...</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>56345346</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Alert>

                <Paper sx={{ p: 3, mb: 4, mt: 2 }}>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                disabled={isLoading}
                            >
                                Select CSV File
                                <input
                                    type="file"
                                    hidden
                                    accept=".csv"
                                    onChange={handleFileChange}
                                />
                            </Button>

                            <Typography variant="body1" color="text.secondary">
                                {file ? file.name : 'No file selected'}
                            </Typography>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={!file || isLoading}
                                sx={{ ml: 'auto' }}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Upload & Approve'}
                            </Button>
                        </Box>
                    </form>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {data && (
                    <Box sx={{ animation: 'fadeIn 0.5s' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                            <Typography variant="h5">
                                {isLoading ? 'Processing in progress...' : 'Processing Results'}
                            </Typography>
                            {isLoading && <CircularProgress size={24} />}
                        </Box>

                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                                    <Typography variant="subtitle2" color="text.secondary">Total Processed</Typography>
                                    <Typography variant="h4">{data.total_processed}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                                    <Typography variant="subtitle2">Approved</Typography>
                                    <Typography variant="h4">{data.approved}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                    <Typography variant="subtitle2">In Progress</Typography>
                                    <Typography variant="h4">{data.in_progress}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                                    <Typography variant="subtitle2">Failed</Typography>
                                    <Typography variant="h4">{data.failed}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                            {data.failed_sessions?.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" color="error" gutterBottom>
                                            Failed Sessions ({data.failed_sessions.length})
                                        </Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                            {data.failed_sessions.map((sessionId, index) => (
                                                <ListItem key={index}>
                                                    <ListItemText primary={sessionId} sx={{ '& .MuiListItemText-primary': { fontFamily: 'monospace', wordBreak: 'break-all' } }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </Grid>
                            )}

                            {data.in_progress_sessions?.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" color="warning.main" gutterBottom>
                                            In Progress Sessions ({data.in_progress_sessions.length})
                                        </Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                            {data.in_progress_sessions.map((sessionId, index) => (
                                                <ListItem key={index}>
                                                    <ListItemText primary={sessionId} sx={{ '& .MuiListItemText-primary': { fontFamily: 'monospace', wordBreak: 'break-all' } }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </ContentContainer>
        </PageContainer>
    );
};

export default ProductCSVApprovePage;